import { HttpClient } from "@angular/common/http";
import { Injectable, OnDestroy, inject } from "@angular/core";
import { Store } from "@ngrx/store";
import { IMqttMessage, MqttService } from "ngx-mqtt";
import {
  firstValueFrom,
  Observable,
  of,
  Subscription,
  throwError,
} from "rxjs";
import {
  filter,
  map,
  switchMap,
  tap,
  withLatestFrom,
} from "rxjs/operators";
import { environment } from "src/environments/environment";
import { AppReserved } from "src/model/class/general classes/app-reserved";
import { DataStorage } from "src/model/class/general classes/data-storage";
import { Scenario } from "src/components/shared-components/class/general-classes/scenario";
import { GenericOperationKind } from "src/model/enum/enumGenericOperationKind";
import { isUserAuthenticated } from "src/state/selectors/authentication.selectors";
import { listScenarios } from "src/state/selectors/scenario.selector";
import { ScenariosState } from "src/state/reducers/scenario.reducer";
import { EventService } from "src/service/event.service";
import { LoggerService } from "src/service/logger.service";
import { MqttClientService } from "src/service/mqtt-client.service";
import { AppSettings } from "src/components/shared-components/class/general-classes/app-settings";
import { GenericApiData } from "src/components/shared-components/class/general-classes/generic-api-data";
import { LogLevel } from "src/components/shared-components/enum/enumLog";
import {
  scenarioDeleteReceivedAction,
  scenarioInsertReceivedAction,
  scenarioUpdateReceivedAction,
  scenarioRunningChangedAction,
  scenarioScheduleAssignedAction,
  scenarioScheduleUnassignedAction,
  scenariosLoadInitStartAction,
  scenariosUnsetKeyStorageAction,
  scheduleDeletedFromScenariosAction,
} from "src/state/actions/scenario.action";
import { scenariosCommandsChangeReceivedAction } from "src/state/actions/scenario-commands.action";
import { ScenarioBody, ScenarioCommand, scenarioCommandBody } from "src/components/shared-components/interfaces/iScenarios";

@Injectable({
  providedIn: "root",
})
/**
 *
 *  Scenario service manage data about scenario
 *
 *
 */
export class ScenariosService
  extends DataStorage
  implements OnDestroy {
  private readonly _http = inject(HttpClient);
  private readonly _eventService = inject(EventService);
  private readonly _mqttService = inject(MqttClientService);
  private readonly _loggerService = inject(LoggerService);
  private readonly _store = inject(Store);

  private _logContext: string = "Scenarios.service";

  private _eventSubscriptions: Array<Subscription> = null;
  private _mqttAddressSubscriptionsMap: Map<string, Subscription> = null;
  private _isMqttSubscribed: boolean = false;

  public needToInitialize: boolean = true;

  /**
   * Public scenario list stream, used for widget scenario configuration in shared-components
   */
  public get scenarioList(): Observable<Scenario[]> {
    return this._store.select(listScenarios).pipe(
      filter(
        (state: ScenariosState) =>
          !!state &&
          state.loaded &&
          !state.errorGeneric &&
          !state.errorNotYetImplemented,
      ),
      map((state: ScenariosState) => state.data?.data || []),
    );
  }

  /**
   * Get the scenarios that a scenario can launch as commands: all but the scenario itself
   * @param idScenario scenario the commands belong to
   */
  public async getScenariosExcept(idScenario: number): Promise<Scenario[]> {
    const scenarios: Scenario[] = await firstValueFrom(this.scenarioList);
    return scenarios.filter((scenario: Scenario) => scenario.id !== idScenario);
  }

  constructor() {
    super();

    this._mqttAddressSubscriptionsMap = new Map<string, Subscription>();

    /** Subscrive event from Event Service */
    this.subscribeEvents();
    this.subscribeMQTTAddresses();
  }

  /**
   * Destroy event service
   */
  ngOnDestroy() {
    //unsubscribe mqtt

    this.unsubscribeToMQTTAddresses();

    this.unsubscribeEvents();
  }

  //#region Subscribe to event-service observable
  subscribeEvents(): void {
    this._eventSubscriptions = new Array<Subscription>();

    this._eventSubscriptions.push(
      this._eventService.OnComeBackOnline.pipe(
        withLatestFrom(this._store.select(isUserAuthenticated)),
        filter((combinedArray: Array<any>) => combinedArray[1]),
        map((combinedArray: Array<any>) => combinedArray[0])
      ).subscribe(() => {
        this._loggerService.log(
          LogLevel.LOG_INFO,
          this._logContext,
          "_eventService",
          "OnComeBackOnline",
          "REACT END"
        );
        this.reloadServiceData();
      })
    );

    this._eventSubscriptions.push(
      this._eventService.OnMakeLogout.subscribe(async () => {

        //TEST
        /*
        this._loggerService.log(LogLevel.LOG_INFO, this._logContext, "_eventService", "OnMakeLogout", "LOCAL MAP-SUBSCRIPTIONS 1: ");
        for (const entry of Array.from(this._mqttAddressSubscriptionsMap.entries())) {
          this._loggerService.log(LogLevel.LOG_INFO, this._logContext, "_eventService", "OnMakeLogout", "LOCAL MAP-SUBSCRIPTIONS 1 - ENTRY: " + entry[0]);
        }

        this._loggerService.log(LogLevel.LOG_INFO, this._logContext, "_eventService", "OnMakeLogout()", "MAP-ARRAY 1.");
        for (const entry of Array.from(map_mqttClient1.entries())) {
          this._loggerService.log(LogLevel.LOG_INFO, this._logContext, "_eventService", "OnMakeLogout()", "MAP-ARRAY 1 Entry:  " + entry[0]);
        }
        this._loggerService.log(LogLevel.LOG_INFO, this._logContext, "_eventService", "OnMakeLogout", "NARTIVE ARRAY 1.");
        for (const entry of Array.from(new Map(Object.entries(this._mqttServiceNative.observables)).entries())) {
          this._loggerService.log(LogLevel.LOG_INFO, this._logContext, "_eventService", "OnMakeLogout", "NARTIVE ARRAY 1 Entry:  " + entry[0]);
        }
        */

        await this.unsubscribeToMQTTAddresses();

        this.needToInitialize = true;
        this._store.dispatch(scenariosUnsetKeyStorageAction());

        //TEST
        /*
        this._loggerService.log(LogLevel.LOG_INFO, this._logContext, "_eventService", "OnMakeLogout", "LOCAL MAP-SUBSCRIPTIONS 2: ");
        for (const entry of Array.from(this._mqttAddressSubscriptionsMap.entries())) {
          this._loggerService.log(LogLevel.LOG_INFO, this._logContext, "_eventService", "OnMakeLogout", "LOCAL MAP-SUBSCRIPTIONS 2 - ENTRY: " + entry[0]);
        }

        this._loggerService.log(LogLevel.LOG_INFO, this._logContext, "_eventService", "OnMakeLogout()", "MAP-ARRAY 2.");
        for (const entry of Array.from(map_mqttClient1.entries())) {
          this._loggerService.log(LogLevel.LOG_INFO, this._logContext, "_eventService", "OnMakeLogout()", "MAP-ARRAY 2 Entry:  " + entry[0]);
        }
        this._loggerService.log(LogLevel.LOG_INFO, this._logContext, "_eventService", "OnMakeLogout", "NARTIVE ARRAY 2.");
        for (const entry of Array.from(new Map(Object.entries(this._mqttServiceNative.observables)).entries())) {
          this._loggerService.log(LogLevel.LOG_INFO, this._logContext, "_eventService", "OnMakeLogout", "NARTIVE ARRAY 2 Entry:  " + entry[0]);
        }
        */
      })
    );

    this._eventSubscriptions.push(
      this._eventService.OnLoginPlantDid.subscribe(() => {
        this.subscribeMQTTAddresses();
      })
    );
  }

  unsubscribeEvents(): void {
    for (let sub of this._eventSubscriptions) {
      sub.unsubscribe();
    }
  }
  //#endregion

  /**
   * Reload service data (es. in case of error)
   */
  public reloadServiceData() {
    this._loggerService.log(
      LogLevel.LOG_INFO,
      this._logContext,
      this._logContext,
      "reloadServiceData()",
      "START"
    );

    this.needToInitialize = true;

    this.Init();
  }

  //#region Mqtt-addresses handling

  async subscribeMQTTAddresses() {
    if (this._isMqttSubscribed) {
      this._loggerService.log(
        LogLevel.LOG_INFO,
        this._logContext,
        this._logContext,
        "subscribeMQTTAddresses",
        "Already subscribed, skipping"
      );
      return;
    }
    this._isMqttSubscribed = true;

    let mqttAddress: string = "";
    let mqttAddressSubcription: Subscription = null;
    let returnedObservable: Observable<IMqttMessage> = null;

    this._loggerService.log(
      LogLevel.LOG_INFO,
      this._logContext,
      this._logContext,
      "subscribeMQTTAddresses",
      "START"
    );

    //#region Subscribing to RESERVED_DB_SCENARIO_CHANGED

    mqttAddress =
      AppSettings.INSTANCE_GUID_VIRTUAL_VARIABLE +
      "_" +
      AppReserved.RESERVED_DB_SCENARIO_CHANGED;

    returnedObservable = await this._mqttService.subscribeTopic(mqttAddress);

    mqttAddressSubcription = returnedObservable
      .pipe(
        map((mqttMessage: IMqttMessage) => {
          return this._mqttService.parseMessage(mqttMessage);
        })
      )
      .subscribe(
        (message: Array<string>) => {

          if (message.length !== 3) {
            this._loggerService.log(
              LogLevel.LOG_ERROR,
              this._logContext,
              "_mqttService.subscribeAddress",
              "AppReserved.RESERVED_DB_SCENARIO_CHANGED",
              "Incorrect format message"
            );
            return;
          }

          const operationKind = parseInt(message[0]);
          const idScenario = parseInt(message[1]);
          const clientID = message[2];

          this._loggerService.log(
            LogLevel.LOG_INFO,
            this._logContext,
            this._logContext,
            "REACT TO RESERVED_DB_SCENARIO_CHANGED",
            "OperationKind : " +
            GenericOperationKind[operationKind] +
            " | idScenario: " +
            idScenario +
            " | clientID: " +
            clientID
          );

          if (operationKind === GenericOperationKind.INSERT) {
            this._store.dispatch(
              scenarioInsertReceivedAction({ idScenario, clientID })
            );
          } else if (operationKind === GenericOperationKind.UPDATE) {
            this._store.dispatch(
              scenarioUpdateReceivedAction({ idScenario, clientID })
            );

            /** The same reserved address notifies the changes on the commands of the scenario
             * too (there is no dedicated one), so the commands list is refreshed as well: the
             * store only reacts if those commands have already been loaded */
            this._store.dispatch(
              scenariosCommandsChangeReceivedAction({ idScenario, clientID })
            );
          } else if (operationKind === GenericOperationKind.DELETE) {
            this._store.dispatch(
              scenarioDeleteReceivedAction({ idScenario, clientID })
            );
          }
        },
        (error) => {
          this._loggerService.log(
            LogLevel.LOG_ERROR,
            this._logContext,
            "_mqttService.subscribeAddress",
            "AppReserved.RESERVED_DB_SCENARIO_CHANGED",
            "ERROR: " + error.message
          );
        }
      );

    await this.checkLocalAddressSubscriptionMap(
      mqttAddress,
      mqttAddressSubcription
    );

    //#endregion

    //#region Subscribing to RESERVED_DB_SCHEDULATION_SCENARIO_CHANGED
    mqttAddress =
      AppSettings.INSTANCE_GUID_VIRTUAL_VARIABLE +
      "_" +
      AppReserved.RESERVED_DB_SCHEDULATION_SCENARIO_CHANGED;

    this._loggerService.log(
      LogLevel.LOG_INFO,
      this._logContext,
      "_mqttService.subscribeAddress",
      "AppReserved.RESERVED_DB_SCHEDULATION_SCENARIO_CHANGED",
      "BEFORE subscribeAddress"
    );

    returnedObservable = await this._mqttService.subscribeTopic(mqttAddress);

    mqttAddressSubcription = returnedObservable
      .pipe(
        map((mqttMessage: IMqttMessage) => {
          return this._mqttService.parseMessage(mqttMessage);
        })
      )
      .subscribe(
        (message: Array<string>) => {
          if (message.length !== 4) {
            this._loggerService.log(
              LogLevel.LOG_ERROR,
              this._logContext,
              "_mqttService.subscribeAddress",
              "AppReserved.RESERVED_DB_SCHEDULATION_SCENARIO_CHANGED",
              "Incorrect format message"
            );
            return;
          }

          const operationKind = parseInt(message[0]);
          const idScenario = parseInt(message[1]);
          const idBigLogic = parseInt(message[2]);
          const clientID = message[3];

          if (operationKind === GenericOperationKind.INSERT) {
            this._store.dispatch(
              scenarioScheduleAssignedAction({
                idScenario,
                idBigLogic,
                clientID,
              })
            );
          } else if (operationKind === GenericOperationKind.DELETE) {
            this._store.dispatch(
              scenarioScheduleUnassignedAction({
                idScenario,
                idBigLogic,
                clientID,
              })
            );
          }
        },
        (error) => {
          this._loggerService.log(
            LogLevel.LOG_ERROR,
            this._logContext,
            "_mqttService.subscribeAddress",
            "AppReserved.RESERVED_DB_SCHEDULATION_SCENARIO_CHANGED",
            "ERROR: " + error.message
          );
        }
      );

    await this.checkLocalAddressSubscriptionMap(
      mqttAddress,
      mqttAddressSubcription
    );

    //#endregion

    //#region Subscribing to RESERVED_DB_SCHEDULATION_CHANGED
    mqttAddress =
      AppSettings.INSTANCE_GUID_VIRTUAL_VARIABLE +
      "_" +
      AppReserved.RESERVED_DB_SCHEDULATION_CHANGED;

    returnedObservable = await this._mqttService.subscribeTopic(mqttAddress);

    mqttAddressSubcription = returnedObservable
      .pipe(
        map((mqttMessage: IMqttMessage) => {
          return this._mqttService.parseMessage(mqttMessage);
        })
      )
      .subscribe(
        (message: Array<string>) => {
          if (message.length <= 2) {
            this._loggerService.log(
              LogLevel.LOG_ERROR,
              this._logContext,
              "_mqttService.subscribeAddress",
              "AppReserved.RESERVED_DB_SCHEDULATION_CHANGED",
              "Incorrect format message"
            );
            return;
          }

          const operationKind = parseInt(message[0]);
          const idBigLogic = parseInt(message[1]);

          /**
           * Manage only Schedule delete because it's the unique operation that could affect scenarios.
           */
          if (operationKind === GenericOperationKind.DELETE) {
            this._store.dispatch(
              scheduleDeletedFromScenariosAction({ idBigLogic })
            );
          }
        },
        (error) => {
          this._loggerService.log(
            LogLevel.LOG_ERROR,
            this._logContext,
            "_mqttService.subscribeAddress",
            "AppReserved.RESERVED_DB_SCHEDULATION_CHANGED",
            "ERROR: " + error.message
          );
        }
      );

    await this.checkLocalAddressSubscriptionMap(
      mqttAddress,
      mqttAddressSubcription
    );

    //#endregion
  }

  public subscribeScenarioRunningEvent(scenario: Scenario) {
    const scenarioAddress =
      AppSettings.INSTANCE_GUID_OMNIA + "_Scenario/" + scenario.id + "/Running";

    this._mqttService
      .subscribeTopic(scenarioAddress)
      .then((observableTopic) => {
        const mqttAddressSubcription = observableTopic.subscribe(
          (message: IMqttMessage) => {
            this._loggerService.log(
              LogLevel.LOG_INFO,
              this._logContext,
              "_mqttService.subscribeAddress",
              AppSettings.INSTANCE_GUID_OMNIA +
              "_Scenario/" +
              scenario.id +
              "/Running",
              "New value read " + JSON.parse(message.payload.toString())
            );

            const running =
              JSON.parse(message.payload.toString()).Message.Value === "1";

            this._store.dispatch(
              scenarioRunningChangedAction({
                idScenario: scenario.id,
                running,
              })
            );
          }
        );

        this.checkLocalAddressSubscriptionMap(
          scenarioAddress,
          mqttAddressSubcription
        );
      });
  }

  async checkLocalAddressSubscriptionMap(
    addressToCheck: string,
    addressSubscription: Subscription
  ): Promise<boolean> {
    let result: boolean = false;
    if (this._mqttAddressSubscriptionsMap.has(addressToCheck)) {
      addressSubscription.unsubscribe();

      this._loggerService.log(
        LogLevel.LOG_INFO,
        this._logContext,
        this._logContext,
        "checkLocalAddressSubscriptionMap()",
        "Address ALREADY present " + addressToCheck
      );
    } else {
      this._mqttAddressSubscriptionsMap.set(
        addressToCheck,
        addressSubscription
      );

      this._loggerService.log(
        LogLevel.LOG_INFO,
        this._logContext,
        this._logContext,
        "checkLocalAddressSubscriptionMap()",
        "NEW address: " + addressToCheck
      );
      result = true;
    }
    return result;
  }

  public publishAddress(address: string, value: string): void {
    this._mqttService.publishAddress(address, value);
  }

  async unsubscribeToMQTTAddresses(): Promise<void> {
    for (const entry of Array.from(
      this._mqttAddressSubscriptionsMap.entries()
    )) {
      entry[1].unsubscribe();

      this._mqttAddressSubscriptionsMap.delete(entry[0]);
    }
  }

  //#endregion

  //#region  Scenario API operations
  /**
   *  Insert new scenario via API
   * @param insertScenario insert new scenario
   */
  public insertScenario(insertScenario: ScenarioBody): Observable<Scenario> {
    return this._http
      .post<Scenario>(
        environment.httpServerRoot +
        AppSettings.API_URL_SCENARIO,
        insertScenario
      )
      .pipe(
        tap((insertedScenario: Scenario) => {
          const mqttAddress =
            AppSettings.INSTANCE_GUID_VIRTUAL_VARIABLE +
            "_" +
            AppReserved.RESERVED_DB_SCENARIO_CHANGED;

          const mqttMessage =
            GenericOperationKind.INSERT.toString() +
            "|" +
            insertedScenario.id.toString();

          /** Fire propagation to all connected client */
          this.publishAddress(mqttAddress, mqttMessage);
        })
      );
  }

  /**
   *  Update existing scenario
   * @param updateScenario update existing scenario data
   * @param idScenario ID of the scenario to update
   */
  public updateScenario(updateScenario: ScenarioBody, idScenario: number): Observable<Scenario> {
    return this._http
      .put<Scenario>(
        environment.httpServerRoot +
        AppSettings.API_URL_SCENARIO +
        "/" +
        idScenario,
        updateScenario
      )
      .pipe(
        tap(() => {
          const mqttAddress =
            AppSettings.INSTANCE_GUID_VIRTUAL_VARIABLE +
            "_" +
            AppReserved.RESERVED_DB_SCENARIO_CHANGED;

          const mqttMessage =
            GenericOperationKind.UPDATE.toString() +
            "|" +
            idScenario.toString();

          /** Fire propagation to all connected client */
          this.publishAddress(mqttAddress, mqttMessage);
        })
      );
  }

  /**
   *  Delete existing scenario
   * @param idScenario ID of the scenario to delete
   */
  public deleteScenario(idScenario: number) {
    return this._http
      .delete(
        environment.httpServerRoot +
        AppSettings.API_URL_SCENARIO +
        "/" +
        idScenario
      )
      .pipe(
        tap(() => {
          const mqttAddress =
            AppSettings.INSTANCE_GUID_VIRTUAL_VARIABLE +
            "_" +
            AppReserved.RESERVED_DB_SCENARIO_CHANGED;

          const mqttMessage =
            GenericOperationKind.DELETE.toString() +
            "|" +
            idScenario.toString();

          /** Fire propagation to all connected client */
          this.publishAddress(mqttAddress, mqttMessage);
        })
      )
      .toPromise();
  }

  /**
   *
   * Get scenario data from API. If api call fallback in error costructor, it set the appropriate flag "_isErrorInGetScenarioListAPIRequest" to
   * the HTTP ERROR CODE, it set the store to NULL and emit on the osbservable a neutral value (IE. empty array).
   *
   */
  public getScenarios(): Observable<GenericApiData> {
    return this._http.get<GenericApiData>(
      environment.httpServerRoot + AppSettings.API_URL_SCENARIO
    );
  }

  /**
   * Get scenario data from API with last modify data store.
   * @param scenarioStore
   * @returns
   */
  public getScenariosByLastModified(
    scenarioStore: GenericApiData
  ): Observable<GenericApiData> {
    const lastAPIModifiedString: string = scenarioStore?.lastAPIModified || "";
    const queryString: string =
      "?lastModified=" + encodeURIComponent(lastAPIModifiedString);

    return this._http
      .get<GenericApiData>(
        environment.httpServerRoot + AppSettings.API_URL_SCENARIO + queryString
      )
      .pipe(
        map((returnScenarioStore: GenericApiData) => {
          if (returnScenarioStore) {
            scenarioStore = returnScenarioStore;
          }

          return scenarioStore;
        })
      );
  }

  /**
   * Get the list of commands for a specific scenario
   * @param idScenario ID of the scenario to get commands for
   * @returns Observable of commands associated to the scenario
   */
  public getScenarioCommandList(idScenario: number): Observable<ScenarioCommand[]> {
    return this._http.get<ScenarioCommand[]>(
      environment.httpServerRoot +
      AppSettings.API_URL_SCENARIO + `/${idScenario}/command`
    );
  }

  /**
   * Add a new command to a specific scenario
   * @param idScenario ID of the scenario to add the command to
   * @param body The command data to be added
   * @returns Observable of the newly added command
   */
  public addScenarioCommand(idScenario: number, body: scenarioCommandBody): Observable<scenarioCommandBody> {
    return this._http.post<scenarioCommandBody>(
      environment.httpServerRoot +
      AppSettings.API_URL_SCENARIO + `/${idScenario}/command`, body
    );
  }

  /**
   * Edit a command in a specific scenario
   * @param idScenario ID of the scenario to edit the command in
   * @param body The command data to be edited
   * @returns Observable of the edited command
   */
  public editScenarioCommand(idScenario: number, idCommand: number, body: scenarioCommandBody): Observable<scenarioCommandBody> {
    return this._http.put<scenarioCommandBody>(
      environment.httpServerRoot +
      AppSettings.API_URL_SCENARIO + `/${idScenario}/command/${idCommand}`, body
    );
  }

  /**
   * Delete a command from a specific scenario
   * @param idScenario ID of the scenario from which to delete the command
   * @param idCommand ID of the command to be deleted
   * @returns Observable of void
   */
  public deleteScenarioCommand(idScenario: number, idCommand: number): Observable<void> {
    return this._http.delete<void>(
      environment.httpServerRoot +
      AppSettings.API_URL_SCENARIO + `/${idScenario}/command/${idCommand}`
    );
  }

  /**
   * Get single scenario by ID from API.
   *  If api call fallback in error costructor, it propagate an Observable notification with .error() function
   */
  public async getScenarioByID(idScenario: number): Promise<Scenario> {
    const queryString: string = "?id=" + idScenario;

    return this._http
      .get<GenericApiData>(
        environment.httpServerRoot + AppSettings.API_URL_SCENARIO + queryString
      )
      .pipe(
        switchMap((returnScenarioList: GenericApiData) => {
          /** Clean API object from unnecessary data
           *  and check if data key is populated: if length is equal to zero, no
           *  scenario is finded
           */

          if (returnScenarioList.data.length > 0) {
            const returnScenario: Scenario = returnScenarioList.data[0];

            return of(returnScenario);
          } else {
            return throwError(new Error());
          }
        })
      )
      .toPromise()
      .then(
        (returnScenario: Scenario) => {
          return returnScenario;
        },
        (error) => {
          throw new Error();
        }
      );
  }
  //#endregion


  /**
 *  Initialize public observable list trasmission:
 *  it should be call in the costructor of all component/page that you need.
 */
  public Init(): void {
    this._store.dispatch(scenariosLoadInitStartAction());
    if (this.needToInitialize) {
      this.needToInitialize = false;
    }
  }
}
